import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';
import SEO from '../components/SEO';
import { ArticleSchema, BreadcrumbSchema } from '../components/StructuredData';
import ShareButtons from '../components/ShareButtons';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const samplePosts = {
  'sample-1': {
    title: 'Why Daily Bookkeeping Beats Monthly Close',
    category: 'Insights',
    created_at: '2025-01-15',
    cover_image: 'https://images.unsplash.com/photo-1643494824830-1e63d776d6ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYmxhY2slMjBhbmQlMjB3aGl0ZSUyMGRlc2slMjBjb2ZmZWV8ZW58MHx8fHwxNzc1ODAzNjA5fDA&ixlib=rb-4.1.0&q=85',
    content: `## The Problem with Monthly Close\n\nTraditional bookkeeping operates on a monthly cycle. You hand over your bank statements, receipts, and invoices at month-end, and your bookkeeper spends days reconciling everything. By the time you see your financials, they're already stale.\n\n## Why T+1 Matters\n\nWith daily bookkeeping (T+1 posting), every cleared transaction is categorized and reconciled within 24 hours. This means:\n\n- **Real-time cash visibility**: Know your exact cash position every morning.\n- **Early anomaly detection**: Spot unauthorized charges or errors immediately.\n- **Accurate burn tracking**: For VC-backed companies, daily burn data means better runway planning.\n- **Faster month-end close**: When 95% of transactions are already posted, your monthly close drops from T+15 to T+3-5.\n\n## How It Works\n\n1. **Automated data ingestion**: Bank feeds, payment gateways, and accounting tools are connected via API.\n2. **ML categorization**: Our AI classifies transactions with >98% accuracy.\n3. **Human review**: Expert bookkeepers handle exceptions and approve postings.\n4. **Daily digest**: You get a short summary of cash, receipts, payments, AR, and anomalies.\n\n## The Bottom Line\n\nDaily bookkeeping isn't just faster - it's a fundamentally different approach to financial operations. It turns your books from a historical record into a real-time decision-making tool.`,
  },
  'sample-2': {
    title: 'GST Compliance for Cross-Border SaaS',
    category: 'Compliance',
    created_at: '2025-01-10',
    content: `## Understanding GST for SaaS\n\nIf you're a SaaS company selling globally from India, GST compliance can be complex. Place-of-supply rules, export classifications, and input tax credits all play a role.\n\n## Key Considerations\n\n- **Domestic sales**: 18% GST on SaaS subscriptions\n- **Exports**: Zero-rated with proper documentation (LUT filing)\n- **Place of supply**: Location of the recipient determines if it's inter-state or intra-state\n- **HSN/SAC codes**: Correct classification is crucial for compliance\n\n## Best Practices\n\n1. Maintain proper documentation for all export transactions\n2. File LUT annually to claim zero-rated exports\n3. Track place-of-supply for each transaction\n4. Reconcile GST returns with books monthly\n5. Prepare for e-invoicing requirements`,
  },
  'sample-3': {
    title: 'Unit Economics Every D2C Founder Should Track',
    category: 'FP&A',
    created_at: '2025-01-05',
    content: `## The Metrics That Matter\n\nFor D2C brands, unit economics determine whether you're building a sustainable business or burning through capital.\n\n## Essential Metrics\n\n### Customer Acquisition Cost (CAC)\nTotal marketing & sales spend divided by new customers acquired. Track this by channel.\n\n### Lifetime Value (LTV)\nAverage order value x purchase frequency x customer lifespan. Compare LTV:CAC ratio (target 3:1+).\n\n### Contribution Margin\nRevenue minus variable costs (COGS, shipping, payment processing). This tells you if each order is profitable before fixed costs.\n\n### Average Order Value (AOV)\nTotal revenue / number of orders. Increasing AOV through bundling or upsells is often cheaper than acquiring new customers.\n\n## Why These Matter\n\nWith daily tracking, you can spot trends early. A declining contribution margin might indicate rising supplier costs. A dropping LTV might signal quality issues. Daily data gives you the signal to act fast.`,
  },
};

export default function BlogPostPage() {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [postId]);

  const fetchPost = async () => {
    if (samplePosts[postId]) {
      setPost(samplePosts[postId]);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/api/blog/${postId}`);
      setPost(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <p className="text-sm text-gray-500">Loading...</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <p className="text-lg font-bold text-black mb-2">Post not found</p>
        <Link to="/blog" className="text-sm text-[#002FA7] font-semibold">Back to blog</Link>
      </div>
    );
  }

  const renderContent = (content) => {
    if (!content) return null;
    return content.split('\n').map((line, i) => {
      if (line.startsWith('### ')) return <h3 key={i} className="text-lg font-bold text-black mt-6 mb-2">{line.replace('### ', '')}</h3>;
      if (line.startsWith('## ')) return <h2 key={i} className="text-xl font-bold text-black mt-8 mb-3">{line.replace('## ', '')}</h2>;
      if (line.startsWith('- **')) {
        const parts = line.replace('- **', '').split('**');
        return <li key={i} className="text-sm text-gray-700 leading-relaxed ml-4"><strong>{parts[0]}</strong>{parts[1]}</li>;
      }
      if (line.startsWith('- ')) return <li key={i} className="text-sm text-gray-700 leading-relaxed ml-4">{line.replace('- ', '')}</li>;
      if (line.match(/^\d+\./)) return <li key={i} className="text-sm text-gray-700 leading-relaxed ml-4 list-decimal">{line.replace(/^\d+\.\s*/, '')}</li>;
      if (line.trim() === '') return <br key={i} />;
      return <p key={i} className="text-sm text-gray-700 leading-relaxed mb-2">{line}</p>;
    });
  };

  const postUrl = `https://www.ventriqo.com/blog/${postId}`;

  return (
    <div>
      <SEO
        title={post.title}
        description={post.excerpt || post.content?.substring(0, 160).replace(/[#*\n]/g, '')}
        path={`/blog/${postId}`}
        type="article"
        image={post.cover_image || undefined}
        article={{ published: post.created_at, category: post.category }}
      />
      <ArticleSchema
        title={post.title}
        description={post.excerpt || post.content?.substring(0, 160)}
        url={postUrl}
        image={post.cover_image}
        datePublished={post.created_at}
      />
      <BreadcrumbSchema items={[{ name: 'Home', path: '/' }, { name: 'Blog', path: '/blog' }, { name: post.title, path: `/blog/${postId}` }]} />

      <section className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <Link to="/blog" data-testid="blog-back-link" className="text-sm text-[#002FA7] font-semibold inline-flex items-center gap-1 mb-8 hover:gap-2 transition-all">
            <ArrowLeft size={14} /> Back to Blog
          </Link>
          {post.category && (
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-4 block">{post.category}</span>
          )}
          <h1 data-testid="blog-post-title" className="text-3xl md:text-4xl font-black tracking-tighter leading-tight text-black mb-4">
            {post.title}
          </h1>
          <p className="text-xs text-gray-500">{post.created_at ? new Date(post.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}</p>
        </div>
      </section>

      {post.cover_image && (
        <section className="border-b border-gray-200">
          <div className="max-w-4xl mx-auto">
            <img src={post.cover_image} alt={post.title} className="w-full h-64 md:h-96 object-cover" />
          </div>
        </section>
      )}

      <section className="bg-white">
        <div data-testid="blog-post-content" className="max-w-3xl mx-auto px-6 py-12">
          {renderContent(post.content)}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <ShareButtons url={postUrl} title={post.title} />
          </div>
        </div>
      </section>
    </div>
  );
}
